import { Link } from 'react-router-dom';
import useHero from './useHero';
import { DESCENT_FRAMES } from './heroSpecimen';
import { formatCoord, magnification } from './format';

// The landing page.
//
// Its one job is to make a stranger want to go looking. The argument it makes is
// the descent: a row of frames from the whole set down to the specimen filling the
// screen behind them, ending in an empty outlined frame — empty because its
// contents are this page. The first rung is the exact view "Start zooming" hands
// you, so the row is a true statement of what the product does rather than a
// decoration about it.
//
// Everything here is computed by the same renderer the visitor's own zooms will
// use, at the visitor's own viewport aspect. Nothing is a stored image. That is the
// difference from the page this replaces, which showed a 2020 JPEG in a palette
// this code cannot produce — an advertisement for an image nobody could generate.
const IntroPanel = () => {

    const { bounds, hero, descent } = useHero();

    const factor = magnification(bounds);

    // The last rung only lights up once its ancestors are all on screen, so the row
    // reads strictly left to right and arriving at it is the payoff. Its box is
    // present from the first frame either way — appearing late would shift the
    // labels underneath it.
    const reached = descent.length === DESCENT_FRAMES;

    return (
        <div id="hero">
            {hero.dataUrl &&
                <img
                    src={hero.dataUrl}
                    alt={`A region of the Mandelbrot set magnified ${factor} times, drawn from the coordinates shown`}
                    className={`hero-image${hero.sharp ? '' : ' is-soft'}`}
                />
            }
            <div className="hero-scrim" />

            <div className="hero-content">
                {/* Broken by hand rather than by measure, as the share page's pitch
                    is: at display sizes a ch-based max-width lands the wrap
                    somewhere different in every clamp step. */}
                <h1 className="hero-title">Find the one<br />that&rsquo;s yours.</h1>
                <p className="hero-sub">
                    Zoom in until something stops you. Claim its coordinates, and no
                    one else can have them.
                </p>

                {/* The signature. Rendered live and arriving one frame at a time,
                    because the frames finishing IS the descent — the motion is the
                    computation, not an effect applied to it. */}
                <div className="descent">
                    {/* One figure, not a list to walk through: role="img" plus a
                        label describes the whole row in a sentence and spares a
                        screen-reader user five unlabelled frames. */}
                    <ol
                        className="descent-rungs"
                        role="img"
                        aria-label={`The way down to this specimen: four steps from the whole set to ${factor} times magnification, ending on the frame you are looking at`}
                    >
                        {Array.from({ length: DESCENT_FRAMES }, (_, index) => {
                            const frame = descent[index];
                            return (
                                <li className="descent-rung" key={index}>
                                    {frame
                                        ? <img src={frame.dataUrl} alt="" />
                                        : <span className="thumb-pending" />}
                                </li>
                            );
                        })}
                        {/* No image: you are looking at this one full size. */}
                        <li className={`descent-rung is-here${reached ? ' is-reached' : ''}`} />
                    </ol>

                    <p className="descent-ends">
                        <span>Whole set</span>
                        <span className="descent-depth">&times;{factor}</span>
                    </p>
                </div>

                {/* The same link, to the same route, as every other "start zooming"
                    in the app — so the action has one name and one destination
                    wherever a visitor meets it. */}
                <Link to="/explore" className="btn-begin">Start zooming</Link>
            </div>

            {/* A caption, not a control: the coordinates are here to show that the
                picture has an address, and they are the address of exactly what is
                on screen, because the region was fitted to this viewport rather
                than cropped to it. */}
            <div className="hero-plate">
                <span className="placard-label">Specimen</span>
                <span className="hero-plate-coords">
                    <span><i className="placard-axis">&#8477;</i> {formatCoord(bounds.realMin)} &rarr; {formatCoord(bounds.realMax)}</span>
                    <span><i className="placard-axis">&#120050;</i> {formatCoord(bounds.imaginaryMin)} &rarr; {formatCoord(bounds.imaginaryMax)}</span>
                </span>
            </div>
        </div>
    );
};

export default IntroPanel;
